/* eslint-disable react/jsx-props-no-spreading */
import React, { useCallback, useMemo, useEffect, useState } from 'react'
import { func, bool } from 'prop-types'
import { useSelector, useDispatch } from 'react-redux'

import Drawer from '@groovehq/internal-design-system/lib/components/Drawer/Drawer'
import Field from '@groovehq/internal-design-system/lib/components/Field/Field'
import ModalBtns from '@groovehq/internal-design-system/lib/components/ModalBtns/ModalBtns'
import Textarea from '@groovehq/internal-design-system/lib/components/Textarea/Textarea'
import Dropdown from '@groovehq/internal-design-system/lib/components/Dropdown/Dropdown'
import Checkbox from '@groovehq/internal-design-system/lib/components/Checkbox/Checkbox'
import DropdownMenu from 'subapps/settings/components/DropdownMenu'
import { useForm, useController } from 'react-hook-form'
import { selectIntegrationCredentialsForId } from 'ducks/integrations'

import { yupResolver } from '@hookform/resolvers/yup'
import * as yup from 'yup'

import { styles as fieldStyles } from '@groovehq/internal-design-system/lib/components/Field/Field.styles'

import { useDrawer } from 'ducks/drawers/hooks'
import { buildDrawerQueryParam } from 'ducks/drawers/util'
import { DRAWER_TYPE_SHOPIFY_REVIEW_INVOICE } from 'ducks/drawers/types'

import doFetchShopInfo from 'ducks/integrations/shopify/operations/doFetchShopInfo'

import { styles } from './SendInvoice.styles'

const { Button } = Dropdown

const formSchema = yup.object().shape({
  to: yup
    .string()
    .email()
    .required(),
  subject: yup.string().required('You need to provide a subject'),
  message: yup.string(),
  from: yup.string(),
  bcc: yup.boolean(),
})

function FormDropdown({ control, name, list }) {
  const {
    field: { onChange, value },
  } = useController({
    name,
    control,
    rules: { required: true },
    defaultValue: '',
  })

  const handleSelectFromEmail = useCallback(
    email => {
      onChange(email)
    },
    [onChange]
  )

  const convertedList = useMemo(
    () => {
      return list.map(val => ({ id: val, name: val, value: val }))
    },
    [list]
  )

  return (
    <Dropdown
      overlay={<DropdownMenu data={convertedList} />}
      onSelect={handleSelectFromEmail}
      selectedKey={value}
    >
      <Button css={styles.dropdownBtn}>{value}</Button>
    </Dropdown>
  )
}

function Footer({ control, onSave, onCancel }) {
  const {
    formState: { isValid },
  } = useController({
    control,
  })
  return (
    <ModalBtns
      saveBtnText="Review invoice"
      tertiaryBtnText="Cancel"
      saveBtnDisabled={!isValid}
      onSave={onSave}
      onClickTertiaryBtn={onCancel}
    />
  )
}

const SendInvoice = ({
  open = true,
  onClose,
  onExit,
  drawerResourceId,
  drawerIntegrationId,
  customerEmail,
}) => {
  const [isLoading, setIsLoading] = useState(true)
  const [bcc, setBccValue] = useState('')
  const [fromEmails, setFromEmails] = useState([])

  const integration = useSelector(state => {
    return selectIntegrationCredentialsForId(state, {
      id: drawerIntegrationId,
    })
  })

  const dispatch = useDispatch()

  const { register, control, getValues, setValue } = useForm({
    mode: 'onBlur',
    resolver: yupResolver(formSchema),
    defaultValues: {
      to: customerEmail || '',
      subject: 'Invoice {{name}}',
      message: '',
      from: fromEmails[0],
      bcc: false,
    },
  })

  const { drawerId, openDrawer } = useDrawer({
    type: DRAWER_TYPE_SHOPIFY_REVIEW_INVOICE,
  })

  const handleReview = useCallback(
    () => {
      const payload = getValues()
      const data = { ...payload, bcc: payload.bcc ? bcc : null }
      openDrawer(drawerResourceId, {
        query: {
          ...buildDrawerQueryParam(
            drawerId,
            'drawerIntegrationId',
            drawerIntegrationId
          ),
        },
        additionalProps: { data },
      })
    },
    [
      openDrawer,
      drawerId,
      drawerResourceId,
      drawerIntegrationId,
      getValues,
      bcc,
    ]
  )

  useEffect(
    () => {
      async function fetchData() {
        const data = await dispatch(doFetchShopInfo(integration.id))
        const contactEmail = data.shop.contactEmail
        setBccValue(contactEmail)
        // This is now set to [contactEmail], but it should also add staffMembers[].email
        // but we don't have access to it (yet
        setFromEmails([contactEmail])
        setValue('from', contactEmail)
        setIsLoading(false)
      }
      if (!integration) return
      fetchData()
    },
    [dispatch, integration, setValue]
  )

  const footer = (
    <Footer control={control} onSave={handleReview} onCancel={onClose} />
  )

  return (
    <Drawer
      title="Send invoice"
      open={open}
      footer={footer}
      onClose={onExit}
      isLoading={isLoading}
    >
      <div className="grui pt-10" css={styles.content}>
        <Field
          css={styles.fullWidth}
          label="To"
          placeholder="Receiver email address"
          htmlType="email"
          {...register('to')}
        />
        <div>
          <div css={fieldStyles.labelBox} className="grui mt-12">
            From
          </div>
          <div css={styles.dropdownContainer}>
            <FormDropdown control={control} list={fromEmails} name="from" />
          </div>
        </div>
        <Field
          css={styles.fullWidth}
          {...register('subject')}
          label="Subject"
        />
        <Textarea
          css={styles.fullWidth}
          rows={3}
          className="grui mb-12"
          label="Custom message (optional)"
          {...register('message')}
        />
        <Checkbox
          id="bcc"
          {...register('bcc')}
        >{`Send bcc to "${bcc}"`}</Checkbox>
      </div>
    </Drawer>
  )
}

SendInvoice.propTypes = {
  onClose: func,
  open: bool.isRequired,
}

SendInvoice.defaultProps = {
  onClose() {},
  onHide() {},
  invoice: {},
  onSaveInvoice() {},
  onShowlastStep() {},
  onShowNextStep() {},
}

export default SendInvoice
